'use client';

import { useMemo, useRef, useState } from 'react';
import { formatNumber, parseNumberList } from '@/lib/browser-mathcalc-tools';
import { consumeToolUsage } from '@/lib/browser-tool-usage';
import {
  chiSquareGoodness,
  chiSquareIndependence,
  geometricMean,
  harmonicMean,
  mean,
  medianOfSorted,
  modes,
  normalCdf,
  percentileInc,
  percentileNearestRank,
  percentileRank,
  quartilesTextbook,
  sortAsc,
  sumSquares,
  variance,
  type ChiSquareResult,
} from '@/lib/list-stats';
import type { ListStatsSlug } from '@/lib/list-stats-slugs';

type Row = { label: string; value: string };

const f = (v: number, d = 6) => (Number.isFinite(v) ? formatNumber(v, d) : 'n/a');
const list = (xs: number[]) => xs.map((x) => f(x)).join(', ');
/** "93.32" → "93.32nd": the suffix follows the last digit as spoken. */
const ordinal = (text: string) => {
  const two = text.replace(/\D/g, '').slice(-2);
  const last = two.slice(-1);
  // Teens (11th, 12th, 13th) only apply to whole numbers; 2.13 reads "two point one three".
  const teen = !text.includes('.') && two.length === 2 && two[0] === '1';
  const suffix = teen
    ? 'th'
    : last === '1'
      ? 'st'
      : last === '2'
        ? 'nd'
        : last === '3'
          ? 'rd'
          : 'th';
  return `${text}${suffix}`;
};

const DEFAULTS: Partial<Record<ListStatsSlug, string>> = {
  'standard-deviation-calculator': '2, 4, 4, 4, 5, 5, 7, 9',
  'variance-calculator': '2, 4, 4, 4, 5, 5, 7, 9',
  'mode-calculator': '3, 7, 5, 3, 9, 7, 3',
  'percentile-calculator': '15, 20, 35, 40, 50',
  'outlier-calculator': '2, 3, 4, 5, 5, 6, 7, 8, 9, 30',
  'geometric-mean-calculator': '1.10, 1.25, 0.95, 1.05',
  'sum-calculator': '19.99, 4.50, 12.00, 7.25',
};

function BoxPlot({ min, q1, q2, q3, max }: Record<'min' | 'q1' | 'q2' | 'q3' | 'max', number>) {
  const span = max - min || 1;
  const x = (v: number) => 10 + ((v - min) / span) * 280;
  return (
    <svg
      aria-label={`Box plot: min ${f(min)}, Q1 ${f(q1)}, median ${f(q2)}, Q3 ${f(q3)}, max ${f(max)}`}
      role="img"
      style={{ width: '100%', maxWidth: 420, height: 'auto', color: 'currentColor' }}
      viewBox="0 0 300 70"
    >
      <line stroke="currentColor" x1={x(min)} x2={x(q1)} y1="30" y2="30" />
      <line stroke="currentColor" x1={x(q3)} x2={x(max)} y1="30" y2="30" />
      <line stroke="currentColor" x1={x(min)} x2={x(min)} y1="20" y2="40" />
      <line stroke="currentColor" x1={x(max)} x2={x(max)} y1="20" y2="40" />
      <rect
        fill="var(--accent-soft, rgba(37,99,235,0.15))"
        height="30"
        stroke="currentColor"
        width={Math.max(1, x(q3) - x(q1))}
        x={x(q1)}
        y="15"
      />
      <line stroke="currentColor" strokeWidth="2.5" x1={x(q2)} x2={x(q2)} y1="15" y2="45" />
      {[min, q1, q2, q3, max].map((v, i) => (
        <text
          fill="currentColor"
          fontSize="9"
          key={i}
          textAnchor="middle"
          x={x(v)}
          y={i % 2 ? 62 : 58}
        >
          {f(v, 3)}
        </text>
      ))}
    </svg>
  );
}

export default function BrowserListStats({
  config,
}: {
  config?: { toolSlug?: string; variant?: string; heading?: string };
}) {
  const variant = (config?.variant ??
    config?.toolSlug ??
    'standard-deviation-calculator') as ListStatsSlug;
  const [input, setInput] = useState(DEFAULTS[variant] ?? '12, 7, 9, 12, 15, 3, 8');
  const [weights, setWeights] = useState('3, 3, 4');
  const [wValues, setWValues] = useState('85, 90, 78');
  const [sample, setSample] = useState(true);
  const [p, setP] = useState('40');
  const [rankOf, setRankOf] = useState('35');
  const [zx, setZx] = useState('85');
  const [zMu, setZMu] = useState('70');
  const [zSd, setZSd] = useState('10');
  const [chiMode, setChiMode] = useState<'fit' | 'table'>('fit');
  const [observed, setObserved] = useState('22, 17, 20, 26, 22, 13');
  const [expected, setExpected] = useState('');
  const [table, setTable] = useState('20 30\n30 20');
  const reported = useRef(false);
  const touch = () => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(config?.toolSlug ?? variant, '').catch(() => undefined);
  };
  const on =
    (set: (v: string) => void) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      touch();
      set(e.target.value);
    };

  const parsed = useMemo(() => parseNumberList(input), [input]);
  const xs = parsed.numbers;
  const s = useMemo(() => sortAsc(xs), [xs]);

  const view = useMemo((): {
    rows: Row[];
    note?: string;
    steps?: { head: string[]; body: string[][] };
    box?: boolean;
    error?: string;
    chi?: ChiSquareResult;
  } => {
    const needList = ![
      'weighted-average-calculator',
      'z-score-calculator',
      'chi-square-calculator',
    ].includes(variant);
    if (needList && !xs.length) return { rows: [], error: 'Enter at least one number.' };
    const n = xs.length;
    const m = n ? mean(xs) : NaN;
    const med = n ? medianOfSorted(s) : NaN;
    const q = n ? quartilesTextbook(s) : { q1: NaN, q2: NaN, q3: NaN };
    const mo = modes(xs);
    const modeText = mo.values.length
      ? `${list(mo.values)} (appears ${mo.freq} times)`
      : 'No mode: every value appears once';
    const sdRows = (label: 'sd' | 'var') => {
      if (n < 2 && sample) return { rows: [], error: 'A sample needs at least two values.' };
      const vS = variance(xs, sample);
      const ss = sumSquares(xs);
      const denom = sample ? n - 1 : n;
      const main: Row[] =
        label === 'sd'
          ? [
              {
                label: sample
                  ? 'Sample standard deviation (s)'
                  : 'Population standard deviation (σ)',
                value: f(Math.sqrt(vS)),
              },
              { label: sample ? 'Sample variance (s²)' : 'Population variance (σ²)', value: f(vS) },
            ]
          : [
              { label: sample ? 'Sample variance (s²)' : 'Population variance (σ²)', value: f(vS) },
              {
                label: sample
                  ? 'Sample standard deviation (s)'
                  : 'Population standard deviation (σ)',
                value: f(Math.sqrt(vS)),
              },
            ];
      return {
        rows: [
          ...main,
          { label: 'Mean (x̄)', value: f(m) },
          { label: 'Count (n)', value: String(n) },
          { label: 'Sum of squares Σ(x − x̄)²', value: f(ss) },
          { label: sample ? 'Divided by n − 1' : 'Divided by n', value: String(denom) },
        ],
        steps:
          n <= 40
            ? {
                head: ['x', 'x − x̄', '(x − x̄)²'],
                body: xs.map((x) => [f(x), f(x - m, 4), f((x - m) ** 2, 4)]),
              }
            : undefined,
        note: sample
          ? 'Sample: divide by n − 1 (Bessel’s correction) when the numbers are a sample of a larger group. Matches Excel STDEV.S / VAR.S.'
          : 'Population: divide by n when you have every member of the group. Matches Excel STDEV.P / VAR.P.',
      };
    };

    switch (variant) {
      case 'standard-deviation-calculator':
        return sdRows('sd');
      case 'variance-calculator':
        return sdRows('var');
      case 'mean-median-mode-calculator':
        return {
          rows: [
            { label: 'Mean', value: f(m) },
            { label: 'Median', value: f(med) },
            { label: 'Mode', value: modeText },
            { label: 'Range', value: f(s[n - 1] - s[0]) },
            { label: 'Count', value: String(n) },
            { label: 'Sorted', value: list(s) },
          ],
        };
      case 'median-calculator': {
        const mid =
          n % 2
            ? `Middle value is number ${(n + 1) / 2} of ${n}`
            : `Average of numbers ${n / 2} and ${n / 2 + 1} of ${n}: (${f(s[n / 2 - 1])} + ${f(s[n / 2])}) ÷ 2`;
        return {
          rows: [
            { label: 'Median', value: f(med) },
            { label: 'How', value: mid },
            { label: 'Sorted', value: list(s) },
            { label: 'Mean (for comparison)', value: f(m) },
          ],
        };
      }
      case 'mode-calculator': {
        const counts = new Map<number, number>();
        for (const x of xs) counts.set(x, (counts.get(x) ?? 0) + 1);
        const freq = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0]);
        return {
          rows: [
            { label: 'Mode', value: modeText },
            {
              label: 'Type',
              value: !mo.values.length
                ? 'No mode'
                : mo.values.length === 1
                  ? 'Unimodal'
                  : mo.values.length === 2
                    ? 'Bimodal'
                    : 'Multimodal',
            },
            { label: 'Count', value: String(n) },
          ],
          steps: {
            head: ['Value', 'Frequency'],
            body: freq.slice(0, 40).map(([v, c]) => [f(v), String(c)]),
          },
        };
      }
      case 'range-calculator':
        return {
          rows: [
            { label: 'Range (max − min)', value: f(s[n - 1] - s[0]) },
            { label: 'Minimum', value: f(s[0]) },
            { label: 'Maximum', value: f(s[n - 1]) },
            { label: 'Interquartile range', value: f(q.q3 - q.q1) },
            { label: 'Count', value: String(n) },
          ],
        };
      case 'sum-calculator':
        return {
          rows: [
            {
              label: 'Sum',
              value: f(
                xs.reduce((a, b) => a + b, 0),
                10,
              ),
            },
            { label: 'Count', value: String(n) },
            { label: 'Mean', value: f(m) },
            { label: 'Minimum', value: f(s[0]) },
            { label: 'Maximum', value: f(s[n - 1]) },
          ],
        };
      case 'percentile-calculator': {
        const pv = Math.min(100, Math.max(0, Number(p) || 0));
        const rv = Number(rankOf);
        return {
          rows: [
            {
              label: `${f(pv)}th percentile (linear, Excel PERCENTILE.INC)`,
              value: f(percentileInc(s, pv)),
            },
            {
              label: `${f(pv)}th percentile (nearest rank)`,
              value: f(percentileNearestRank(s, pv)),
            },
            ...(Number.isFinite(rv) && rankOf.trim()
              ? [{ label: `Percentile rank of ${f(rv)}`, value: `${f(percentileRank(s, rv), 2)}%` }]
              : []),
            { label: 'Sorted', value: list(s) },
          ],
          note: 'There is no single standard definition of a percentile; linear interpolation is what Excel, Google Sheets and NumPy use by default.',
        };
      }
      case 'quartile-calculator':
      case 'interquartile-range-calculator':
      case 'five-number-summary-calculator':
      case 'outlier-calculator': {
        if (n < 4) return { rows: [], error: 'Enter at least four numbers.' };
        const iqr = q.q3 - q.q1;
        const lo = q.q1 - 1.5 * iqr;
        const hi = q.q3 + 1.5 * iqr;
        const outliers = xs.filter((x) => x < lo || x > hi);
        const excel = { q1: percentileInc(s, 25), q3: percentileInc(s, 75) };
        const five: Row[] = [
          { label: 'Minimum', value: f(s[0]) },
          { label: 'Q1 (first quartile)', value: f(q.q1) },
          { label: 'Median (Q2)', value: f(q.q2) },
          { label: 'Q3 (third quartile)', value: f(q.q3) },
          { label: 'Maximum', value: f(s[n - 1]) },
        ];
        const fences: Row[] = [
          { label: 'Lower fence (Q1 − 1.5 × IQR)', value: f(lo) },
          { label: 'Upper fence (Q3 + 1.5 × IQR)', value: f(hi) },
          { label: 'Outliers', value: outliers.length ? list(outliers) : 'None' },
        ];
        const rows =
          variant === 'outlier-calculator'
            ? [
                ...fences,
                { label: 'IQR', value: f(iqr) },
                { label: 'Q1', value: f(q.q1) },
                { label: 'Q3', value: f(q.q3) },
              ]
            : variant === 'interquartile-range-calculator'
              ? [
                  { label: 'Interquartile range (Q3 − Q1)', value: f(iqr) },
                  ...five.slice(1, 4),
                  ...fences,
                ]
              : variant === 'five-number-summary-calculator'
                ? [
                    ...five,
                    { label: 'Interquartile range', value: f(iqr) },
                    { label: 'Range', value: f(s[n - 1] - s[0]) },
                  ]
                : [
                    ...five.slice(1, 4),
                    { label: 'Interquartile range', value: f(iqr) },
                    ...five.filter((_, i) => i === 0 || i === 4),
                  ];
        return {
          rows: [
            ...rows,
            { label: 'Excel QUARTILE.INC gives Q1, Q3', value: `${f(excel.q1)}, ${f(excel.q3)}` },
          ],
          box: true,
          note: 'Quartiles use the median-of-halves method taught in most textbooks and used by TI-83/84 calculators. Excel’s interpolated values are shown for comparison.',
        };
      }
      case 'coefficient-of-variation-calculator': {
        if (n < 2) return { rows: [], error: 'Enter at least two numbers.' };
        const sd = Math.sqrt(variance(xs, sample));
        return {
          rows: [
            {
              label: 'Coefficient of variation',
              value: m ? `${f((sd / Math.abs(m)) * 100, 4)}%` : 'Undefined (mean is 0)',
            },
            {
              label: sample ? 'Sample standard deviation' : 'Population standard deviation',
              value: f(sd),
            },
            { label: 'Mean', value: f(m) },
          ],
          note: 'CV = standard deviation ÷ mean × 100%. It only makes sense for data on a ratio scale with a true zero (not °C or °F).',
        };
      }
      case 'geometric-mean-calculator': {
        const g = geometricMean(xs);
        return {
          rows: [
            { label: 'Geometric mean', value: Number.isNaN(g) ? 'Needs all values above 0' : f(g) },
            { label: 'Arithmetic mean', value: f(m) },
            {
              label: 'Harmonic mean',
              value: Number.isNaN(g) ? 'Needs all values above 0' : f(harmonicMean(xs)),
            },
            { label: 'Count', value: String(n) },
          ],
          note: 'For growth factors (1.10 = +10%), the geometric mean minus 1 is the average growth rate per period.',
        };
      }
      case 'weighted-average-calculator': {
        const v = parseNumberList(wValues).numbers;
        const w = parseNumberList(weights).numbers;
        if (!v.length) return { rows: [], error: 'Enter at least one value.' };
        if (v.length !== w.length)
          return {
            rows: [],
            error: `Enter one weight per value: ${v.length} values, ${w.length} weights.`,
          };
        const sw = w.reduce((a, b) => a + b, 0);
        if (!sw) return { rows: [], error: 'The weights add up to zero.' };
        const swx = v.reduce((a, x, i) => a + x * w[i], 0);
        return {
          rows: [
            { label: 'Weighted average', value: f(swx / sw) },
            { label: 'Σ(value × weight)', value: f(swx) },
            { label: 'Σ weights', value: f(sw) },
            { label: 'Plain average (unweighted)', value: f(mean(v)) },
          ],
          steps: {
            head: ['Value', 'Weight', 'Value × weight'],
            body: v.map((x, i) => [f(x), f(w[i]), f(x * w[i])]),
          },
        };
      }
      case 'z-score-calculator': {
        const x = Number(zx);
        const mu = Number(zMu);
        const sd = Number(zSd);
        if (!(sd > 0)) return { rows: [], error: 'Standard deviation must be above 0.' };
        const z = (x - mu) / sd;
        const left = normalCdf(z);
        return {
          rows: [
            { label: 'Z-score', value: f(z, 4) },
            { label: 'P(Z < z), area to the left', value: f(left, 5) },
            { label: 'P(Z > z), area to the right', value: f(1 - left, 5) },
            { label: 'Two-tailed p-value', value: f(2 * Math.min(left, 1 - left), 5) },
            { label: 'Percentile', value: ordinal(f(left * 100, 2)) },
          ],
          note: 'z = (x − μ) ÷ σ. Probabilities assume a normal distribution.',
        };
      }
      case 'chi-square-calculator': {
        let r: ChiSquareResult | string;
        if (chiMode === 'fit') {
          r = chiSquareGoodness(
            parseNumberList(observed).numbers,
            parseNumberList(expected).numbers,
          );
        } else {
          const rows = table
            .split('\n')
            .map((line) => parseNumberList(line).numbers)
            .filter((row) => row.length);
          r = chiSquareIndependence(rows);
        }
        if (typeof r === 'string') return { rows: [], error: r };
        return {
          rows: [
            { label: 'Chi-square statistic χ²', value: f(r.statistic, 4) },
            { label: 'Degrees of freedom', value: String(r.df) },
            { label: 'p-value', value: r.p < 0.0001 ? '< 0.0001' : f(r.p, 4) },
            {
              label: 'At α = 0.05',
              value:
                r.p < 0.05
                  ? 'Significant: reject the null hypothesis'
                  : 'Not significant: no evidence against the null',
            },
          ],
          chi: r,
          note: r.lowExpected
            ? `${r.lowExpected} expected ${r.lowExpected === 1 ? 'count is' : 'counts are'} below 5, so the chi-square approximation may be unreliable; consider Fisher’s exact test.`
            : 'All expected counts are 5 or more, so the chi-square approximation is reasonable.',
        };
      }
      default:
        return { rows: [] };
    }
  }, [
    variant,
    xs,
    s,
    sample,
    p,
    rankOf,
    wValues,
    weights,
    zx,
    zMu,
    zSd,
    chiMode,
    observed,
    expected,
    table,
  ]);

  const usesList = ![
    'weighted-average-calculator',
    'z-score-calculator',
    'chi-square-calculator',
  ].includes(variant);
  const usesSample = [
    'standard-deviation-calculator',
    'variance-calculator',
    'coefficient-of-variation-calculator',
  ].includes(variant);
  const q = xs.length >= 4 ? quartilesTextbook(s) : null;

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Statistics</span>
        <h2 className="tool-live-title">{config?.heading ?? 'Statistics calculator'}</h2>
        <p className="tool-live-subtitle">
          Results update as you type. Everything runs in your browser.
        </p>
      </div>
      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            {usesList ? (
              <label className="tool-live-field tool-live-field-full">
                <span>Numbers (separated by commas, spaces or new lines)</span>
                <textarea onChange={on(setInput)} rows={6} value={input} />
              </label>
            ) : null}
            {usesSample ? (
              <label className="tool-live-field">
                <span>Data is a</span>
                <select
                  onChange={(e) => {
                    touch();
                    setSample(e.target.value === 'sample');
                  }}
                  value={sample ? 'sample' : 'population'}
                >
                  <option value="sample">Sample (n − 1)</option>
                  <option value="population">Whole population (n)</option>
                </select>
              </label>
            ) : null}
            {variant === 'percentile-calculator' ? (
              <>
                <label className="tool-live-field">
                  <span>Percentile (0-100)</span>
                  <input inputMode="decimal" onChange={on(setP)} type="text" value={p} />
                </label>
                <label className="tool-live-field">
                  <span>Percentile rank of value (optional)</span>
                  <input inputMode="decimal" onChange={on(setRankOf)} type="text" value={rankOf} />
                </label>
              </>
            ) : null}
            {variant === 'weighted-average-calculator' ? (
              <>
                <label className="tool-live-field tool-live-field-full">
                  <span>Values (grades, prices, scores)</span>
                  <textarea onChange={on(setWValues)} rows={3} value={wValues} />
                </label>
                <label className="tool-live-field tool-live-field-full">
                  <span>Weights (credits, quantities, percentages), same order</span>
                  <textarea onChange={on(setWeights)} rows={3} value={weights} />
                </label>
              </>
            ) : null}
            {variant === 'z-score-calculator' ? (
              <>
                <label className="tool-live-field">
                  <span>Value (x)</span>
                  <input inputMode="decimal" onChange={on(setZx)} type="text" value={zx} />
                </label>
                <label className="tool-live-field">
                  <span>Mean (μ)</span>
                  <input inputMode="decimal" onChange={on(setZMu)} type="text" value={zMu} />
                </label>
                <label className="tool-live-field">
                  <span>Standard deviation (σ)</span>
                  <input inputMode="decimal" onChange={on(setZSd)} type="text" value={zSd} />
                </label>
              </>
            ) : null}
            {variant === 'chi-square-calculator' ? (
              <>
                <label className="tool-live-field tool-live-field-full">
                  <span>Test</span>
                  <select
                    onChange={(e) => {
                      touch();
                      setChiMode(e.target.value as 'fit' | 'table');
                    }}
                    value={chiMode}
                  >
                    <option value="fit">Goodness of fit (one row of counts)</option>
                    <option value="table">Test of independence (contingency table)</option>
                  </select>
                </label>
                {chiMode === 'fit' ? (
                  <>
                    <label className="tool-live-field tool-live-field-full">
                      <span>Observed counts</span>
                      <textarea onChange={on(setObserved)} rows={2} value={observed} />
                    </label>
                    <label className="tool-live-field tool-live-field-full">
                      <span>Expected counts or proportions (blank = all equal)</span>
                      <textarea onChange={on(setExpected)} rows={2} value={expected} />
                    </label>
                  </>
                ) : (
                  <label className="tool-live-field tool-live-field-full">
                    <span>Table: one row per line, columns separated by spaces or commas</span>
                    <textarea onChange={on(setTable)} rows={5} spellCheck={false} value={table} />
                  </label>
                )}
              </>
            ) : null}
          </div>
          {usesList && parsed.ignored.length ? (
            <p className="tool-live-panel-note">
              Ignored {parsed.ignored.length} non-numeric{' '}
              {parsed.ignored.length === 1 ? 'entry' : 'entries'}:{' '}
              {parsed.ignored.slice(0, 6).join(', ')}
            </p>
          ) : null}
        </section>
        <section className="tool-live-preview">
          {view.error ? (
            <p className="tool-live-panel-note">{view.error}</p>
          ) : (
            <>
              <div className="tool-html-meta-grid">
                {view.rows.map((row) => (
                  <div className="tool-live-field" key={row.label}>
                    <span>{row.label}</span>
                    <input readOnly type="text" value={row.value} />
                  </div>
                ))}
              </div>
              {view.box && q ? (
                <BoxPlot max={s[s.length - 1]} min={s[0]} q1={q.q1} q2={q.q2} q3={q.q3} />
              ) : null}
              {view.chi && view.chi.expected.length ? (
                <p className="tool-live-panel-note">
                  Expected counts:{' '}
                  {view.chi.expected.map((row) => row.map((e) => f(e, 2)).join(', ')).join(' / ')}
                </p>
              ) : null}
              {view.steps ? (
                <div className="tool-data-table-shell">
                  <table className="tool-data-table">
                    <thead>
                      <tr>
                        {view.steps.head.map((h) => (
                          <th key={h}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {view.steps.body.map((cells, i) => (
                        <tr key={i}>
                          {cells.map((c, j) => (
                            <td key={j}>{c}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {view.note ? <p className="tool-live-panel-note">{view.note}</p> : null}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
